import * as React from 'react';
import { useEffect, useState } from 'react';
import { StripeSetupCardForm } from './StripeSetupCardForm';
import { FormLayoutWidget } from '../../../../../widgets/layout/FormLayoutWidget';
import { LoaderSpinner } from '../../../../../widgets/LoaderWidget';
import { useSystem } from '../../../../../hooks/useSystem';
import { CreditCardWidget } from './CreditCardWidget';
import { PaymentConfigurationModel } from '@journeyapps-platform/lib-journey-core';
import { GroupedButtons } from '../../../../../widgets/buttons/GroupedButtons';
import { Btn, ButtonType } from '../../../../../widgets/buttons/ButtonWidget';
import { styled } from '../../../../../theme';
import { observer } from 'mobx-react';

export interface CardManagementFormWidgetProps {
  next?: Btn;
  back: Btn;
  org_id: string;
  cardAdded?: (id: string) => any;
  generateCard: (card: PaymentConfigurationModel, element: React.JSX.Element) => any;
  desc?: string;
  legalUrl?: string;
}

export const CardManagementFormWidget: React.FC<CardManagementFormWidgetProps> = observer((props) => {
  const system = useSystem();
  const org = system.orgStore.current_org;

  useEffect(() => {
    org?.loadPaymentConfigurations();
  }, [props.org_id]);

  const [showCardSetup, setShowCardSetup] = useState(false);

  const cards = org?.paymentConfigurations.items || [];

  if (!org || org?.paymentConfigurations.loading) {
    return (
      <S.LoadingContainer>
        <LoaderSpinner />
      </S.LoadingContainer>
    );
  }

  const addingFirstcard = cards.length === 0;
  if (addingFirstcard || showCardSetup) {
    return (
      <>
        <S.Description>Fill out the form below to add a new card to your organization.</S.Description>
        <S.Description style={{ opacity: 0.6 }}>
          Your details will be securely transferred to our payment provider, and are never stored on our servers.
        </S.Description>
        <S.Description style={{ opacity: 0.6 }}>
          By providing your card information, you allow JourneyApps to charge future payments to your card in accordance{' '}
          {props.legalUrl ? (
            <S.Anchor href={props.legalUrl} target="_blank">
              with our terms.
            </S.Anchor>
          ) : (
            'with our terms.'
          )}
        </S.Description>
        <StripeSetupCardForm
          org_id={props.org_id}
          onComplete={(config_id) => {
            org?.loadPaymentConfigurations();
            props.cardAdded?.(config_id);
            setShowCardSetup(false);
          }}
          backBtn={{
            ...props.back,
            action: (event, loading) => {
              if (addingFirstcard) {
                props.back.action(event, loading);
              } else {
                setShowCardSetup(false);
              }
            }
          }}
        />
      </>
    );
  }
  return (
    <>
      {props.desc ? <S.Description>{props.desc}</S.Description> : null}
      <FormLayoutWidget
        sections={[
          {
            key: 'select',
            elements: [
              <S.CardsContainer>
                {cards.length === 0 ? <LoaderSpinner /> : null}
                {cards.map((card) => {
                  return (
                    <React.Fragment key={card.model.id}>
                      {props.generateCard(card, <CreditCardWidget width={200} card={card.model} />)}
                    </React.Fragment>
                  );
                })}
              </S.CardsContainer>
            ]
          },
          {
            key: 'buttons',
            elements: [
              <GroupedButtons
                btns={[
                  {
                    type: ButtonType.SECONDARY,
                    ...props.back
                  },
                  {
                    type: ButtonType.SECONDARY,
                    label: 'Add card',
                    action: () => {
                      setShowCardSetup(true);
                    }
                  },
                  ...(props.next
                    ? [
                        {
                          type: ButtonType.PRIMARY,
                          ...props.next
                        }
                      ]
                    : [])
                ]}
              />
            ]
          }
        ]}
      />
    </>
  );
});

namespace S {
  export const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const Description = styled.p`
    font-size: 14px;
    color: ${(p) => p.theme.text.primary};
    padding-bottom: 20px;
    max-width: 500px;
  `;

  export const CardsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 30px;
    column-gap: 10px;
    row-gap: 20px;
  `;

  export const Anchor = styled.a`
    color: ${(p) => p.theme.text.highlight};
    text-decoration: none;
  `;
}
